import React, { useState, useEffect, useCallback } from "react";
import { useAuthenticated, useNotify } from "react-admin";
import axiosWithAuth from "../../utils/axiosWithAuth";

// MUI v4
import {
  makeStyles,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  LinearProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Box,
} from "@material-ui/core";
import Alert from "@mui/material/Alert";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import WarningIcon from "@material-ui/icons/Warning";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";

const API_BASE = `/admin/predictor-import`;

async function apiGet(path) {
  const res = await axiosWithAuth.get(`${API_BASE}${path}`);
  return res.data;
}

async function apiPost(path, data) {
  const res = await axiosWithAuth.post(`${API_BASE}${path}`, data);
  return res.data;
}

async function apiPut(path, data) {
  const res = await axiosWithAuth.put(`${API_BASE}${path}`, data);
  return res.data;
}

async function apiDelete(path) {
  const res = await axiosWithAuth.delete(`${API_BASE}${path}`);
  return res.data;
}

const SKIP = "__skip__";
const STEPS = [
  "Select & Upload",
  "Map Columns",
  "Validate FKs",
  "Preview",
  "Execute",
];

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  stepper: { backgroundColor: "transparent", marginBottom: theme.spacing(2) },
  paper: { padding: theme.spacing(3), marginTop: theme.spacing(2) },
  stepActions: {
    marginTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(1),
  },
  tableContainer: { maxHeight: 420, marginTop: theme.spacing(1) },
  required: { color: theme.palette.error.main, fontWeight: "bold" },
  chip: { margin: theme.spacing(0.25) },
  unknownList: {
    fontFamily: "monospace",
    fontSize: 13,
    wordBreak: "break-all",
  },
  warnPaper: {
    padding: theme.spacing(2),
    border: `2px solid ${theme.palette.error.main}`,
    backgroundColor: "#fff8f8",
    marginBottom: theme.spacing(2),
  },
  resultPaper: {
    padding: theme.spacing(2),
    backgroundColor: "#f0fff4",
    border: `2px solid ${theme.palette.success.main || "#388e3c"}`,
  },
  sectionLabel: { fontWeight: 600, marginBottom: theme.spacing(1) },
  progressWrap: { marginTop: theme.spacing(2) },
}));

// ─── localStorage helpers for mapping templates ───────────────────────────────
function saveTemplate(key, mapping) {
  try {
    localStorage.setItem(`ctp_mapping_${key}`, JSON.stringify(mapping));
  } catch {}
}
function loadTemplate(key) {
  try {
    const raw = localStorage.getItem(`ctp_mapping_${key}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────
const PredictorImport = () => {
  useAuthenticated();
  const classes = useStyles();
  const notify = useNotify();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Step 1
  const [predictors, setPredictors] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [selectedPredictor, setSelectedPredictor] = useState(null);
  const [yearColumns, setYearColumns] = useState([]); // for wide-table predictors
  const [file, setFile] = useState(null);
  const [sheetsUrl, setSheetsUrl] = useState("");
  const [exportLoading, setExportLoading] = useState(false);

  // After parse
  const [sessionId, setSessionId] = useState(null);
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [staticDbColumns, setStaticDbColumns] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [preview, setPreview] = useState([]);
  const [sheetOptions, setSheetOptions] = useState([]);
  const [selectedSheetName, setSelectedSheetName] = useState("");
  const [needsSheetSelection, setNeedsSheetSelection] = useState(false);

  // Step 2
  const [columnMapping, setColumnMapping] = useState({});

  // Step 3
  const [validationResult, setValidationResult] = useState(null);

  // Step 5
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Load predictor list on mount
  useEffect(() => {
    apiGet("/predictors")
      .then((d) => setPredictors(d.data || []))
      .catch(() => notify("Failed to load predictor list", "error"));
  }, [notify]);

  // When predictor changes: fetch year columns for wide tables
  useEffect(() => {
    if (!selectedKey) return;
    const pred = predictors.find((p) => p.key === selectedKey);
    setSelectedPredictor(pred || null);
    setYearColumns([]);
    if (pred && pred.tableType === "wide") {
      apiGet(`/year-columns/${selectedKey}`)
        .then((d) => setYearColumns(d.yearColumns || []))
        .catch(() => {});
    }
  }, [selectedKey, predictors]);

  // All DB columns available for mapping (static + dynamic year cols)
  const allDbColumns = [...staticDbColumns, ...yearColumns];

  // Required DB columns for the selected predictor
  const requiredDbCols = selectedPredictor?.requiredColumns || [];

  // Derive FK management paths from validation result (backend returns these too)
  const fkPaths =
    validationResult?.fkManagementPaths ||
    selectedPredictor?.fkManagementPaths ||
    {};

  const applyParsedSheetData = useCallback(
    (d) => {
      setExcelHeaders(d.headers || []);
      setStaticDbColumns(d.staticDbColumns || []);
      setRowCount(d.rowCount || 0);
      setPreview(d.preview || []);

      const saved = loadTemplate(selectedKey);
      const initial = {};
      (d.headers || []).forEach((h) => {
        if (saved && saved[h] !== undefined) {
          initial[h] = saved[h];
        } else if (d.autoMapping && d.autoMapping[h]) {
          initial[h] = d.autoMapping[h];
        } else {
          initial[h] = SKIP;
        }
      });

      setColumnMapping(initial);
      setValidationResult(null);
      setBranchGroupResult(null);
      setImportResult(null);
      setQuickCreateOpen(false);
      setQuickCreateRows([]);
      setQuickCreateError(null);
      setBranchGroupMapperOpen(false);
      setBranchGroupAssignments({});
      setBranchGroupOriginalAssignments({});
      setBranchGroupMappings([]);
      setGroupOptions([]);
      setGroupNameDrafts({});
      setNewGroupForm({ groupId: "", groupName: "" });
      setBranchGroupSearch("");
      setBranchGroupManagerError(null);
    },
    [selectedKey],
  );

  // ── Step 1: Parse ────────────────────────────────────────────────────────────
  const handleParse = async () => {
    if (!selectedKey || !file) {
      setError("Please select a predictor and upload an Excel file.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("predictorKey", selectedKey);
      const res = await axiosWithAuth.post(`${API_BASE}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = res.data;
      setSessionId(d.sessionId);
      setSheetOptions(d.sheets || []);
      setSelectedSheetName(
        d.selectedSheetName || (d.sheets || [])[0]?.name || "",
      );
      applyParsedSheetData(d);

      const hasMultipleSheets = (d.sheets || []).length > 1;
      setNeedsSheetSelection(hasMultipleSheets);

      if (hasMultipleSheets) {
        notify(
          "Multiple sheets detected. Select which sheet to import.",
          "info",
        );
      } else {
        setActiveStep(1);
      }
    } catch (e) {
      setError(e.response?.data?.error || "Failed to parse file.");
    } finally {
      setLoading(false);
    }
  };

  const handleParseFromSheets = async () => {
    if (!selectedKey || !sheetsUrl.trim()) {
      setError(
        "Please select a predictor and enter a public Google Sheets URL.",
      );
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const d = await apiPost("/upload-from-sheets", {
        predictorKey: selectedKey,
        sheetsUrl: sheetsUrl.trim(),
      });
      setSessionId(d.sessionId);
      setSheetOptions(d.sheets || []);
      setSelectedSheetName(
        d.selectedSheetName || (d.sheets || [])[0]?.name || "",
      );
      applyParsedSheetData(d);

      const hasMultipleSheets = (d.sheets || []).length > 1;
      setNeedsSheetSelection(hasMultipleSheets);

      if (hasMultipleSheets) {
        notify(
          "Multiple sheets detected. Select which sheet to import.",
          "info",
        );
      } else {
        setActiveStep(1);
      }
    } catch (e) {
      setError(e.response?.data?.error || "Failed to import Google Sheets.");
    } finally {
      setLoading(false);
    }
  };

  const handleExportTable = async () => {
    if (!selectedKey) return;

    setExportLoading(true);
    try {
      const response = await axiosWithAuth.get(
        `${API_BASE}/export/${selectedKey}`,
        {
          responseType: "blob",
        },
      );
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const fileName = match?.[1] || `${selectedKey}-export.xlsx`;
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data]);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      notify("Live table export downloaded", "success");
    } catch (e) {
      notify(
        "Failed to export table: " + (e.response?.data?.error || e.message),
        "error",
      );
    } finally {
      setExportLoading(false);
    }
  };

  const handleSelectSheetAndContinue = async () => {
    if (!sessionId || !selectedKey || !selectedSheetName) {
      setError("Please select a sheet.");
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const d = await apiPost("/select-sheet", {
        sessionId,
        predictorKey: selectedKey,
        sheetName: selectedSheetName,
      });
      applyParsedSheetData(d);
      setNeedsSheetSelection(false);
      setActiveStep(1);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to load selected sheet.");
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Mapping helpers ──────────────────────────────────────────────────
  const unmappedRequired = requiredDbCols.filter(
    (dbCol) => !Object.values(columnMapping).includes(dbCol),
  );
  const canProceedMapping = unmappedRequired.length === 0;

  const handleMappingChange = (excelCol, dbCol) => {
    setColumnMapping((prev) => ({ ...prev, [excelCol]: dbCol }));
  };

  // ── Step 3: Validate ─────────────────────────────────────────────────────────
  const handleValidate = async () => {
    setError(null);
    setLoading(true);
    try {
      const result = await apiPost("/validate", {
        sessionId,
        predictorKey: selectedKey,
        columnMapping,
      });
      setValidationResult(result);
      if (result.valid) {
        setBranchGroupResult(null);
        await handleCheckBranchGroups();
      }
    } catch (e) {
      setError(e.response?.data?.error || "Validation failed.");
    } finally {
      setLoading(false);
    }
  };

  // Step 3 - quick create
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [quickCreateCol, setQuickCreateCol] = useState(null); // which fkColumn
  const [quickCreateRows, setQuickCreateRows] = useState([]);
  const [quickCreateLoading, setQuickCreateLoading] = useState(false);
  const [quickCreateError, setQuickCreateError] = useState(null);

  // Step 3 - branch group validation
  const [branchGroupResult, setBranchGroupResult] = useState(null);
  const [branchGroupLoading, setBranchGroupLoading] = useState(false);
  const [branchGroupMapperOpen, setBranchGroupMapperOpen] = useState(false);
  const [groupOptions, setGroupOptions] = useState([]);
  const [groupOptionsLoading, setGroupOptionsLoading] = useState(false);
  const [groupActionLoading, setGroupActionLoading] = useState(false);
  const [branchGroupManagerError, setBranchGroupManagerError] = useState(null);
  const [branchGroupMappings, setBranchGroupMappings] = useState([]);
  const [branchGroupAssignments, setBranchGroupAssignments] = useState({});
  const [branchGroupOriginalAssignments, setBranchGroupOriginalAssignments] =
    useState({});
  const [branchGroupSearch, setBranchGroupSearch] = useState("");
  const [groupNameDrafts, setGroupNameDrafts] = useState({});
  const [newGroupForm, setNewGroupForm] = useState({
    groupId: "",
    groupName: "",
  });
  const [assignBranchGroupLoading, setAssignBranchGroupLoading] =
    useState(false);

  const clearParsedState = () => {
    setSessionId(null);
    setExcelHeaders([]);
    setStaticDbColumns([]);
    setRowCount(0);
    setPreview([]);
    setSheetOptions([]);
    setSelectedSheetName("");
    setNeedsSheetSelection(false);
    setColumnMapping({});
    setValidationResult(null);
    setBranchGroupResult(null);
    setImportResult(null);
    setConfirmOpen(false);
    setQuickCreateOpen(false);
    setQuickCreateRows([]);
    setQuickCreateError(null);
    setQuickCreateLoading(false);
    setBranchGroupMapperOpen(false);
    setBranchGroupAssignments({});
    setBranchGroupOriginalAssignments({});
    setBranchGroupMappings([]);
    setGroupOptions([]);
    setGroupOptionsLoading(false);
    setGroupActionLoading(false);
    setBranchGroupManagerError(null);
    setGroupNameDrafts({});
    setNewGroupForm({ groupId: "", groupName: "" });
    setBranchGroupSearch("");
    setAssignBranchGroupLoading(false);
    setBranchGroupLoading(false);
    setError(null);
  };

  // FK field definitions for quick-create dialogs
  const FK_CREATE_FIELDS = {
    instituteId: [
      {
        name: "institute_id",
        label: "Institute ID  (e.g. GFTINIELITAMT)",
        required: true,
      },
      { name: "institute_name", label: "Institute Name", required: true },
      { name: "rank", label: "Rank", required: false },
    ],
    branchId: [
      { name: "branchId", label: "Branch ID  (e.g. CSEIT)", required: true },
      { name: "title", label: "Branch Title", required: true },
    ],
    categoryId: [
      { name: "categoryId", label: "Category ID  (e.g. OPEN)", required: true },
      { name: "title", label: "Title", required: true },
    ],
  };

  const FK_CREATE_LABELS = {
    instituteId: "Institute",
    branchId: "Branch",
    categoryId: "Category",
  };

  const createQuickCreateRow = (fkCol, seed = {}) => ({
    _rowId: `${fkCol}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    ...seed,
  });

  const getQuickCreatePkField = (fkCol) =>
    (FK_CREATE_FIELDS[fkCol] || []).find((field) => field.required)?.name ||
    (FK_CREATE_FIELDS[fkCol] || [])[0]?.name ||
    "id";

  const normalizeGroupIds = (groupIds) =>
    [...new Set((groupIds || []).filter(Boolean))].sort();

  const buildQuickCreateErrorMessage = (items = []) =>
    (items || [])
      .slice(0, 6)
      .map((item) => `Row ${(item.index ?? 0) + 1}: ${item.error}`)
      .join(" | ");

  const openQuickCreate = (fkCol) => {
    const pkField = getQuickCreatePkField(fkCol);
    const missingIds = validationResult?.unknowns?.[fkCol] || [];
    setQuickCreateCol(fkCol);
    setQuickCreateRows(
      (missingIds.length > 0 ? missingIds : [""]).map((missingId) =>
        createQuickCreateRow(fkCol, missingId ? { [pkField]: missingId } : {}),
      ),
    );
    setQuickCreateError(null);
    setQuickCreateOpen(true);
  };

  const handleQuickCreateRowChange = (rowId, fieldName, value) => {
    setQuickCreateRows((prev) =>
      prev.map((row) =>
        row._rowId === rowId ? { ...row, [fieldName]: value } : row,
      ),
    );
  };

  const handleQuickCreateAddRow = () => {
    setQuickCreateRows((prev) => [
      ...prev,
      createQuickCreateRow(quickCreateCol),
    ]);
  };

  const handleQuickCreateRemoveRow = (rowId) => {
    setQuickCreateRows((prev) => prev.filter((row) => row._rowId !== rowId));
  };

  const handleQuickCreateSave = async () => {
    const entries = quickCreateRows
      .map(({ _rowId, ...row }) => row)
      .filter((row) =>
        Object.values(row).some((value) => String(value || "").trim() !== ""),
      );

    if (entries.length === 0) {
      setQuickCreateError("Add at least one row before saving.");
      return;
    }

    setQuickCreateError(null);
    setQuickCreateLoading(true);
    try {
      const quickCreateResult = await apiPost("/quick-create-bulk", {
        predictorKey: selectedKey,
        fkColumn: quickCreateCol,
        entries,
      });
      notify(
        `${FK_CREATE_LABELS[quickCreateCol]} bulk update: created ${quickCreateResult.createdCount}, skipped ${quickCreateResult.skippedExisting}${quickCreateResult.invalidCount ? `, issues ${quickCreateResult.invalidCount}` : ""}`,
        quickCreateResult.invalidCount ? "warning" : "success",
      );

      if (quickCreateResult.invalidCount) {
        setQuickCreateError(
          buildQuickCreateErrorMessage(quickCreateResult.errors || []),
        );
      } else {
        setQuickCreateOpen(false);
      }

      // Re-run FK validation automatically
      setValidationResult(null);
      setBranchGroupResult(null);
      const result = await apiPost("/validate", {
        sessionId,
        predictorKey: selectedKey,
        columnMapping,
      });
      setValidationResult(result);
      if (result.valid) {
        await handleCheckBranchGroups();
      }
    } catch (e) {
      setQuickCreateError(
        e.response?.data?.error || "Failed to create records.",
      );
    } finally {
      setQuickCreateLoading(false);
    }
  };

  const handleCheckBranchGroups = async () => {
    setBranchGroupLoading(true);
    try {
      const result = await apiPost("/validate-branch-groups", {
        sessionId,
        predictorKey: selectedKey,
        columnMapping,
      });
      setBranchGroupResult(result);
    } catch (e) {
      notify(
        "Branch group check failed: " + (e.response?.data?.error || e.message),
        "error",
      );
    } finally {
      setBranchGroupLoading(false);
    }
  };

  const loadBranchGroupManagerData = async () => {
    if (!selectedKey) return;

    setGroupOptionsLoading(true);
    setBranchGroupManagerError(null);
    try {
      const d = await apiGet(`/branch-groups/mappings/${selectedKey}`);
      if (d.applicable === false) {
        setGroupOptions([]);
        setBranchGroupMappings([]);
        setBranchGroupAssignments({});
        setBranchGroupOriginalAssignments({});
        setGroupNameDrafts({});
        setBranchGroupManagerError(
          d.message ||
            "Branch group management is not applicable for this predictor.",
        );
        return;
      }

      const nextGroupOptions = d.groupOptions || [];
      const nextMappings = d.data || [];
      const nextAssignments = {};

      nextMappings.forEach((branch) => {
        nextAssignments[branch.branchId] = [...(branch.groupIds || [])];
      });

      setGroupOptions(nextGroupOptions);
      setBranchGroupMappings(nextMappings);
      setBranchGroupAssignments(nextAssignments);
      setBranchGroupOriginalAssignments(
        Object.fromEntries(
          Object.entries(nextAssignments).map(([branchId, groupIds]) => [
            branchId,
            [...groupIds],
          ]),
        ),
      );
      setGroupNameDrafts(
        Object.fromEntries(
          nextGroupOptions.map((group) => [
            group.groupId,
            group.groupName || "",
          ]),
        ),
      );
    } catch (e) {
      const message =
        e.response?.data?.error || e.message || "Failed to load branch groups.";
      setBranchGroupManagerError(message);
      notify(`Failed to load branch groups: ${message}`, "error");
    } finally {
      setGroupOptionsLoading(false);
    }
  };

  const handleCreateBranchGroup = async () => {
    const payload = {
      groupId: newGroupForm.groupId.trim(),
      groupName: newGroupForm.groupName.trim(),
    };

    if (!payload.groupId || !payload.groupName) {
      setBranchGroupManagerError("Group ID and group name are required.");
      return;
    }

    setGroupActionLoading(true);
    setBranchGroupManagerError(null);
    try {
      await apiPost("/branch-groups", payload);
      setNewGroupForm({ groupId: "", groupName: "" });
      notify("Branch group created", "success");
      await loadBranchGroupManagerData();
    } catch (e) {
      const message =
        e.response?.data?.error ||
        e.message ||
        "Failed to create branch group.";
      setBranchGroupManagerError(message);
      notify(message, "error");
    } finally {
      setGroupActionLoading(false);
    }
  };

  const handleUpdateBranchGroupName = async (groupId) => {
    const groupName = String(groupNameDrafts[groupId] || "").trim();

    if (!groupName) {
      setBranchGroupManagerError("Group name is required.");
      return;
    }

    setGroupActionLoading(true);
    setBranchGroupManagerError(null);
    try {
      await apiPut(`/branch-groups/${groupId}`, { groupName });
      notify(`Updated ${groupId}`, "success");
      await loadBranchGroupManagerData();
    } catch (e) {
      const message =
        e.response?.data?.error ||
        e.message ||
        "Failed to update branch group.";
      setBranchGroupManagerError(message);
      notify(message, "error");
    } finally {
      setGroupActionLoading(false);
    }
  };

  const handleDeleteBranchGroup = async (groupId) => {
    if (!window.confirm(`Delete branch group '${groupId}'?`)) {
      return;
    }

    setGroupActionLoading(true);
    setBranchGroupManagerError(null);
    try {
      await apiDelete(`/branch-groups/${groupId}`);
      notify(`Deleted ${groupId}`, "success");
      await loadBranchGroupManagerData();
      await handleCheckBranchGroups();
    } catch (e) {
      const message =
        e.response?.data?.error ||
        e.message ||
        "Failed to delete branch group.";
      setBranchGroupManagerError(message);
      notify(message, "error");
    } finally {
      setGroupActionLoading(false);
    }
  };

  const openBranchGroupMapper = async () => {
    setBranchGroupMapperOpen(true);
    setBranchGroupSearch("");
    await loadBranchGroupManagerData();
  };

  const handleBranchGroupsChange = (branchId, value) => {
    setBranchGroupAssignments((prev) => ({
      ...prev,
      [branchId]: normalizeGroupIds(
        typeof value === "string" ? value.split(",") : value,
      ),
    }));
  };

  const handleAssignBranchGroups = async () => {
    const assignments = branchGroupMappings
      .map((branch) => {
        const branchId = branch.branchId;
        const current = normalizeGroupIds(
          branchGroupAssignments[branchId] || [],
        );
        const original = normalizeGroupIds(
          branchGroupOriginalAssignments[branchId] || [],
        );

        if (current.join("::") === original.join("::")) {
          return null;
        }

        return {
          branchId,
          groupIds: current,
        };
      })
      .filter(Boolean);

    if (assignments.length === 0) {
      notify("No branch-group changes to save", "warning");
      return;
    }

    setAssignBranchGroupLoading(true);
    try {
      const d = await apiPost("/branch-groups/assign-bulk", {
        predictorKey: selectedKey,
        assignments,
        replaceExisting: true,
      });
      notify(
        `Branch-group mappings updated. Created: ${d.createdMappings}, removed: ${d.removedMappings}, unchanged: ${d.skippedExisting}`,
        "success",
      );
      await loadBranchGroupManagerData();
      await handleCheckBranchGroups();
    } catch (e) {
      notify(
        "Failed to assign branch groups: " +
          (e.response?.data?.error || e.message),
        "error",
      );
    } finally {
      setAssignBranchGroupLoading(false);
    }
  };

  // ── Step 5: Execute ──────────────────────────────────────────────────────────
  const handleExecute = async () => {
    setConfirmOpen(false);
    setError(null);
    setLoading(true);
    try {
      const result = await apiPost("/execute", {
        sessionId,
        predictorKey: selectedKey,
        columnMapping,
      });
      setImportResult(result);
      saveTemplate(selectedKey, columnMapping);
      notify(
        `Import complete — ${result.rowsInserted} rows inserted`,
        "success",
      );
    } catch (e) {
      setError(e.response?.data?.error || "Import failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    clearParsedState();
    setFile(null);
    setSheetsUrl("");
    setSelectedKey("");
    setSelectedPredictor(null);
    setYearColumns([]);
  };

  // ─── Step renderers ──────────────────────────────────────────────────────────
  const renderStep0 = () => (
    <Paper className={classes.paper}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" className={classes.sectionLabel}>
          1. Select Predictor
        </Typography>
        <Button
          variant="outlined"
          size="small"
          disabled={!selectedKey || exportLoading}
          onClick={handleExportTable}
        >
          {exportLoading ? <CircularProgress size={18} /> : "Export To Excel"}
        </Button>
      </Box>
      <FormControl variant="outlined" fullWidth style={{ marginBottom: 20 }}>
        <InputLabel>Predictor / Table</InputLabel>
        <Select
          value={selectedKey}
          onChange={(e) => {
            clearParsedState();
            setSelectedKey(e.target.value);
            setFile(null);
            setSheetsUrl("");
          }}
          label="Predictor / Table"
        >
          {predictors.map((p) => (
            <MenuItem key={p.key} value={p.key}>
              {p.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="h6" className={classes.sectionLabel}>
        2. Upload Excel File
      </Typography>
      <input
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        id="excel-upload"
        type="file"
        onChange={(e) => {
          clearParsedState();
          setFile(e.target.files[0] || null);
        }}
      />
      <label htmlFor="excel-upload">
        <Button
          variant="outlined"
          component="span"
          startIcon={<CloudUploadIcon />}
        >
          Choose File
        </Button>
      </label>
      {file && (
        <Typography variant="body2" style={{ marginTop: 8 }}>
          Selected: <strong>{file.name}</strong> (
          {(file.size / 1024).toFixed(1)} KB)
        </Typography>
      )}

      <Box mt={3}>
        <Typography variant="h6" className={classes.sectionLabel}>
          3. Import Public Google Sheets Link
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label="Google Sheets URL"
          placeholder="https://docs.google.com/spreadsheets/d/..."
          value={sheetsUrl}
          onChange={(e) => {
            clearParsedState();
            setSheetsUrl(e.target.value);
          }}
          helperText="Public Google Sheets links only. The shared workbook flow and sheet picker will be reused."
        />
      </Box>

      {selectedPredictor?.tableType === "wide" && yearColumns.length > 0 && (
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            Year columns currently in DB:{" "}
            {yearColumns.map((c) => (
              <Chip key={c} label={c} size="small" className={classes.chip} />
            ))}
          </Typography>
        </Box>
      )}

      {sessionId && sheetOptions.length > 1 && (
        <Box mt={2}>
          <Typography variant="h6" className={classes.sectionLabel}>
            4. Select Sheet
          </Typography>
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Sheet</InputLabel>
            <Select
              value={selectedSheetName}
              onChange={(e) => setSelectedSheetName(e.target.value)}
              label="Sheet"
            >
              {sheetOptions.map((sheet) => (
                <MenuItem key={sheet.name} value={sheet.name}>
                  {sheet.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ marginTop: 6, display: "block" }}
          >
            This workbook has multiple sheets. Select the sheet to import.
          </Typography>
        </Box>
      )}

      {needsSheetSelection && (
        <Alert severity="info" style={{ marginTop: 12 }}>
          Multiple sheets detected. Please choose a sheet and continue.
        </Alert>
      )}

      {error && (
        <Alert severity="error" style={{ marginTop: 12 }}>
          {error}
        </Alert>
      )}
      <div className={classes.stepActions}>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedKey || !file || loading}
          onClick={handleParse}
        >
          {loading ? <CircularProgress size={20} /> : "Parse File"}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          disabled={!selectedKey || !sheetsUrl.trim() || loading}
          onClick={handleParseFromSheets}
        >
          {loading ? <CircularProgress size={20} /> : "Import Sheets"}
        </Button>
        {sessionId && sheetOptions.length > 1 && (
          <Button
            variant="contained"
            color="secondary"
            disabled={!selectedSheetName || loading}
            onClick={handleSelectSheetAndContinue}
          >
            {loading ? <CircularProgress size={20} /> : "Use Selected Sheet"}
          </Button>
        )}
      </div>
    </Paper>
  );

  const renderStep1 = () => (
    <Paper className={classes.paper}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="h6" className={classes.sectionLabel}>
          Map Excel Columns → DB Columns
        </Typography>
        <Box>
          <Button
            size="small"
            onClick={() => saveTemplate(selectedKey, columnMapping)}
            style={{ marginRight: 8 }}
          >
            Save Template
          </Button>
          <Button
            size="small"
            onClick={() => {
              const t = loadTemplate(selectedKey);
              if (t) {
                setColumnMapping((prev) => ({ ...prev, ...t }));
                notify("Template loaded", "info");
              } else {
                notify("No saved template for this predictor", "warning");
              }
            }}
          >
            Load Template
          </Button>
        </Box>
      </Box>

      {unmappedRequired.length > 0 && (
        <Alert severity="warning" style={{ marginBottom: 12 }}>
          Required columns not yet mapped:{" "}
          {unmappedRequired.map((c) => (
            <strong key={c}> {c}</strong>
          ))}
        </Alert>
      )}

      <TableContainer
        className={classes.tableContainer}
        component={Paper}
        variant="outlined"
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Excel Column</strong>
              </TableCell>
              <TableCell>
                <strong>Maps To DB Column</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {excelHeaders.map((h) => {
              return (
                <TableRow key={h}>
                  <TableCell>
                    {h}
                    {requiredDbCols.includes(
                      allDbColumns.find((d) => d === h),
                    ) && <span className={classes.required}> *</span>}
                  </TableCell>
                  <TableCell>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Select
                        value={columnMapping[h] || SKIP}
                        onChange={(e) => handleMappingChange(h, e.target.value)}
                      >
                        <MenuItem value={SKIP}>— Skip —</MenuItem>
                        <Divider />
                        {allDbColumns.map((col) => (
                          <MenuItem key={col} value={col}>
                            {col}
                            {requiredDbCols.includes(col) && (
                              <span className={classes.required}> *</span>
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography
        variant="caption"
        color="textSecondary"
        style={{ marginTop: 8, display: "block" }}
      >
        * = required FK column
      </Typography>

      <div className={classes.stepActions}>
        <Button onClick={() => setActiveStep(0)}>Back</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!canProceedMapping}
          onClick={() => {
            setValidationResult(null);
            setBranchGroupResult(null);
            setActiveStep(2);
          }}
        >
          Next: Validate FKs
        </Button>
      </div>
    </Paper>
  );

  const renderStep2 = () => {
    const hasUnknowns =
      validationResult &&
      Object.keys(validationResult.unknowns || {}).length > 0;
    const filteredBranchGroupMappings = (branchGroupMappings || []).filter(
      (branch) => {
        const query = branchGroupSearch.trim().toLowerCase();
        if (!query) return true;

        const groupText = (branch.groups || [])
          .map((group) => `${group.groupId} ${group.groupName || ""}`)
          .join(" ")
          .toLowerCase();

        return (
          branch.branchId.toLowerCase().includes(query) ||
          String(branch.title || "")
            .toLowerCase()
            .includes(query) ||
          groupText.includes(query)
        );
      },
    );

    return (
      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.sectionLabel}>
          Validate Foreign Keys
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Checks that all instituteId, branchId and categoryId values in your
          Excel exist in the database. Unknown IDs must be created before
          importing — use the bulk quick-create tools below.
        </Typography>

        {!validationResult && !loading && (
          <Button variant="contained" color="primary" onClick={handleValidate}>
            Run Validation
          </Button>
        )}

        {loading && (
          <Box className={classes.progressWrap}>
            <CircularProgress size={28} />
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 8 }}
            >
              Checking FK references…
            </Typography>
          </Box>
        )}

        {validationResult && !loading && (
          <>
            {validationResult.valid ? (
              <Alert
                severity="success"
                icon={<CheckCircleOutlineIcon />}
                style={{ marginBottom: 12 }}
              >
                All FK values are valid. You may proceed.
              </Alert>
            ) : (
              <Alert
                severity="error"
                icon={<ErrorOutlineIcon />}
                style={{ marginBottom: 12 }}
              >
                Some FK values were not found in the database. Use Bulk Quick
                Create below to add missing records, then re-run validation.
              </Alert>
            )}

            {hasUnknowns &&
              Object.entries(validationResult.unknowns).map(([col, ids]) => (
                <Accordion key={col} defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" width="100%">
                      <Typography style={{ flex: 1 }}>
                        <strong>{col}</strong> — {ids.length} unknown ID
                        {ids.length !== 1 ? "s" : ""}
                      </Typography>
                      {FK_CREATE_FIELDS[col] && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          style={{ marginRight: 8, whiteSpace: "nowrap" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openQuickCreate(col);
                          }}
                        >
                          + Bulk Quick Create
                        </Button>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails style={{ flexDirection: "column", gap: 8 }}>
                    <Typography className={classes.unknownList}>
                      {ids.join(", ")}
                    </Typography>
                    {fkPaths[col] && (
                      <Typography variant="body2">
                        <a
                          href={`#${fkPaths[col]}`}
                          onClick={(e) => {
                            e.preventDefault();
                            window.location.hash = fkPaths[col];
                          }}
                          style={{ fontSize: 13 }}
                        >
                          Manage in full list →
                        </a>
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}

            {/* ── Branch Group Validation ── */}
            <Box mt={2} p={2} border="1px solid #e0e0e0" borderRadius={4}>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography
                  variant="subtitle1"
                  style={{ flex: 1, fontWeight: 600 }}
                >
                  Branch Group Mapping Check
                </Typography>
                <Box display="flex" style={{ gap: 8 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={openBranchGroupMapper}
                  >
                    Manage Branch Groups
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={branchGroupLoading}
                    onClick={handleCheckBranchGroups}
                  >
                    {branchGroupLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      "Check Branch Groups"
                    )}
                  </Button>
                </Box>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Verifies that every branch in the upload is assigned to at least
                one branch group. Branches without a group will not appear in
                the app predictor results. This remains warn-only and does not
                block import.
              </Typography>
              {branchGroupResult && !branchGroupLoading && (
                <Box mt={1}>
                  {!branchGroupResult.applicable ? (
                    <Alert severity="info" style={{ marginTop: 8 }}>
                      {branchGroupResult.message ||
                        "Branch group validation is not applicable for this predictor."}
                    </Alert>
                  ) : branchGroupResult.valid ? (
                    <Alert
                      severity="success"
                      icon={<CheckCircleOutlineIcon />}
                      style={{ marginTop: 8 }}
                    >
                      All {branchGroupResult.totalBranches} branches have a
                      group mapping. ✓
                    </Alert>
                  ) : (
                    <>
                      <Alert
                        severity="warning"
                        icon={<WarningIcon />}
                        style={{ marginTop: 8 }}
                      >
                        <strong>
                          {branchGroupResult.unmappedBranches.length} branch
                          {branchGroupResult.unmappedBranches.length !== 1
                            ? "es"
                            : ""}{" "}
                          have no group mapping:
                        </strong>{" "}
                        <span className={classes.unknownList}>
                          {branchGroupResult.unmappedBranches
                            .map((b) => b.id ?? b)
                            .join(", ")}
                        </span>
                        <br />
                        <Typography variant="caption" color="textSecondary">
                          You can proceed with import, but these branches may be
                          hidden in downstream predictor grouping.
                        </Typography>
                      </Alert>
                    </>
                  )}
                </Box>
              )}
            </Box>

            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setValidationResult(null);
                setBranchGroupResult(null);
                handleValidate();
              }}
              style={{ marginTop: 12 }}
            >
              Re-run Validation
            </Button>
          </>
        )}

        {error && (
          <Alert severity="error" style={{ marginTop: 12 }}>
            {error}
          </Alert>
        )}

        <div className={classes.stepActions}>
          <Button onClick={() => setActiveStep(1)}>Back</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!validationResult?.valid}
            onClick={() => setActiveStep(3)}
          >
            Next: Preview
          </Button>
        </div>

        {/* ── Quick Create Dialog ── */}
        <Dialog
          open={quickCreateOpen}
          onClose={() => setQuickCreateOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Bulk Quick Create —{" "}
            {FK_CREATE_LABELS[quickCreateCol] || quickCreateCol}
          </DialogTitle>
          <DialogContent>
            {quickCreateError && (
              <Alert severity="error" style={{ marginBottom: 12 }}>
                {quickCreateError}
              </Alert>
            )}
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Add multiple records in one pass. Missing IDs are prefilled when
              available.
            </Typography>
            {quickCreateRows.map((row, index) => (
              <Box
                key={row._rowId}
                mb={2}
                p={2}
                border="1px solid #eee"
                borderRadius={4}
              >
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle2">Row {index + 1}</Typography>
                  <Button
                    size="small"
                    onClick={() => handleQuickCreateRemoveRow(row._rowId)}
                    disabled={
                      quickCreateLoading || quickCreateRows.length === 1
                    }
                  >
                    Remove
                  </Button>
                </Box>
                {(FK_CREATE_FIELDS[quickCreateCol] || []).map((field) => (
                  <TextField
                    key={`${row._rowId}-${field.name}`}
                    label={field.label}
                    required={field.required}
                    fullWidth
                    variant="outlined"
                    size="small"
                    style={{ marginBottom: 16 }}
                    value={row[field.name] || ""}
                    onChange={(e) =>
                      handleQuickCreateRowChange(
                        row._rowId,
                        field.name,
                        e.target.value,
                      )
                    }
                  />
                ))}
              </Box>
            ))}
            <Button
              variant="outlined"
              size="small"
              onClick={handleQuickCreateAddRow}
              disabled={quickCreateLoading}
            >
              Add Row
            </Button>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setQuickCreateOpen(false)}
              disabled={quickCreateLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleQuickCreateSave}
              color="primary"
              variant="contained"
              disabled={quickCreateLoading}
            >
              {quickCreateLoading ? (
                <CircularProgress size={18} />
              ) : (
                "Create Rows & Re-validate"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Branch Group Mapper Dialog ── */}
        <Dialog
          open={branchGroupMapperOpen}
          onClose={() => setBranchGroupMapperOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Branch Group Management</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Manage group definitions and bulk edit branch mappings for the
              selected predictor.
            </Typography>

            {branchGroupManagerError && (
              <Alert severity="error" style={{ marginBottom: 12 }}>
                {branchGroupManagerError}
              </Alert>
            )}

            {groupOptionsLoading ? (
              <Box mt={2} mb={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                <Box mb={3} p={2} border="1px solid #eee" borderRadius={4}>
                  <Typography variant="subtitle1" style={{ marginBottom: 12 }}>
                    Create Group
                  </Typography>
                  <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                    <TextField
                      label="Group ID"
                      variant="outlined"
                      size="small"
                      value={newGroupForm.groupId}
                      onChange={(e) =>
                        setNewGroupForm((prev) => ({
                          ...prev,
                          groupId: e.target.value,
                        }))
                      }
                    />
                    <TextField
                      label="Group Name"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={newGroupForm.groupName}
                      onChange={(e) =>
                        setNewGroupForm((prev) => ({
                          ...prev,
                          groupName: e.target.value,
                        }))
                      }
                    />
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={handleCreateBranchGroup}
                      disabled={groupActionLoading}
                    >
                      {groupActionLoading ? (
                        <CircularProgress size={18} />
                      ) : (
                        "Create"
                      )}
                    </Button>
                  </Box>
                </Box>

                <Box mb={3} p={2} border="1px solid #eee" borderRadius={4}>
                  <Typography variant="subtitle1" style={{ marginBottom: 12 }}>
                    Existing Groups
                  </Typography>
                  {groupOptions.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      No branch groups found.
                    </Typography>
                  ) : (
                    groupOptions.map((group) => (
                      <Box
                        key={group.groupId}
                        display="flex"
                        alignItems="center"
                        mb={1}
                        style={{ gap: 12 }}
                      >
                        <TextField
                          label="Group ID"
                          variant="outlined"
                          size="small"
                          value={group.groupId}
                          disabled
                          style={{ width: 220 }}
                        />
                        <TextField
                          label="Group Name"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={groupNameDrafts[group.groupId] || ""}
                          onChange={(e) =>
                            setGroupNameDrafts((prev) => ({
                              ...prev,
                              [group.groupId]: e.target.value,
                            }))
                          }
                        />
                        <Button
                          size="small"
                          color="primary"
                          variant="outlined"
                          onClick={() =>
                            handleUpdateBranchGroupName(group.groupId)
                          }
                          disabled={groupActionLoading}
                        >
                          Save Name
                        </Button>
                        <Button
                          size="small"
                          color="secondary"
                          variant="outlined"
                          onClick={() => handleDeleteBranchGroup(group.groupId)}
                          disabled={groupActionLoading}
                        >
                          Delete
                        </Button>
                      </Box>
                    ))
                  )}
                </Box>

                <Box mb={2}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Search Branches or Groups"
                    value={branchGroupSearch}
                    onChange={(e) => setBranchGroupSearch(e.target.value)}
                  />
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ display: "block", marginTop: 6 }}
                  >
                    Showing {filteredBranchGroupMappings.length} of{" "}
                    {branchGroupMappings.length} branches.
                  </Typography>
                </Box>

                {filteredBranchGroupMappings.map((branch) => {
                  const branchLabel = branch.title
                    ? `${branch.branchId} - ${branch.title}`
                    : branch.branchId;
                  const isUnmapped = (branch.groupIds || []).length === 0;

                  return (
                    <Box
                      key={branch.branchId}
                      mb={2}
                      p={2}
                      border="1px solid #eee"
                      borderRadius={4}
                    >
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {branchLabel}
                        </Typography>
                        {isUnmapped && (
                          <Chip
                            size="small"
                            color="secondary"
                            label="Unmapped"
                          />
                        )}
                      </Box>
                      <FormControl variant="outlined" fullWidth size="small">
                        <InputLabel>Groups</InputLabel>
                        <Select
                          multiple
                          value={branchGroupAssignments[branch.branchId] || []}
                          onChange={(e) =>
                            handleBranchGroupsChange(
                              branch.branchId,
                              e.target.value,
                            )
                          }
                          label="Groups"
                          renderValue={(selected) =>
                            (selected || [])
                              .map((groupId) => {
                                const group = groupOptions.find(
                                  (item) => item.groupId === groupId,
                                );
                                return group
                                  ? `${group.groupId} - ${group.groupName}`
                                  : groupId;
                              })
                              .join(", ")
                          }
                        >
                          {groupOptions.map((group) => (
                            <MenuItem key={group.groupId} value={group.groupId}>
                              {group.groupId}
                              {group.groupName ? ` - ${group.groupName}` : ""}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  );
                })}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setBranchGroupMapperOpen(false)}
              disabled={assignBranchGroupLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignBranchGroups}
              color="primary"
              variant="contained"
              disabled={assignBranchGroupLoading || groupOptionsLoading}
            >
              {assignBranchGroupLoading ? (
                <CircularProgress size={18} />
              ) : (
                "Save Mapping Changes"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    );
  };

  const renderStep3 = () => {
    // Build mapped preview: transform preview rows using columnMapping
    const mappedHeaders = Object.entries(columnMapping)
      .filter(([, dbCol]) => dbCol && dbCol !== SKIP)
      .map(([excelCol, dbCol]) => ({ excelCol, dbCol }));

    const mappedRows = preview.map((row) => {
      const out = {};
      mappedHeaders.forEach(({ excelCol, dbCol }) => {
        out[dbCol] = row[excelCol];
      });
      return out;
    });

    return (
      <Paper className={classes.paper}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="h6" className={classes.sectionLabel}>
            Data Preview (first {preview.length} of {rowCount} rows)
          </Typography>
          <Chip label={`${rowCount} rows`} color="primary" />
        </Box>

        <TableContainer
          className={classes.tableContainer}
          component={Paper}
          variant="outlined"
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {mappedHeaders.map(({ dbCol }) => (
                  <TableCell key={dbCol}>
                    <strong>{dbCol}</strong>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {mappedRows.map((row, i) => (
                <TableRow key={i}>
                  {mappedHeaders.map(({ dbCol }) => (
                    <TableCell key={dbCol}>
                      {row[dbCol] !== null && row[dbCol] !== undefined
                        ? String(row[dbCol])
                        : "—"}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <div className={classes.stepActions}>
          <Button onClick={() => setActiveStep(2)}>Back</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setActiveStep(4)}
          >
            Next: Execute
          </Button>
        </div>
      </Paper>
    );
  };

  const renderStep4 = () => {
    if (importResult) {
      return (
        <Paper className={classes.paper}>
          <Paper className={classes.resultPaper}>
            <Box display="flex" alignItems="center" mb={1}>
              <CheckCircleOutlineIcon
                style={{ color: "#388e3c", marginRight: 8 }}
              />
              <Typography variant="h6">Import Successful</Typography>
            </Box>
            <Typography>
              <strong>{importResult.rowsInserted}</strong> rows inserted into{" "}
              <code>{importResult.tableName}</code> in{" "}
              {(importResult.durationMs / 1000).toFixed(1)}s
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 4 }}
            >
              Column mapping saved as template for next time.
            </Typography>
          </Paper>
          <div className={classes.stepActions}>
            <Button variant="outlined" onClick={handleReset}>
              Import Another File
            </Button>
          </div>
        </Paper>
      );
    }

    return (
      <Paper className={classes.paper}>
        <Paper className={classes.warnPaper}>
          <Box display="flex" alignItems="center" mb={1}>
            <WarningIcon style={{ color: "#d32f2f", marginRight: 8 }} />
            <Typography variant="h6" style={{ color: "#d32f2f" }}>
              Destructive Action
            </Typography>
          </Box>
          <Typography variant="body1">
            This will <strong>TRUNCATE</strong> the table{" "}
            <code>{selectedPredictor?.label}</code> and insert{" "}
            <strong>{rowCount}</strong> rows.
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 6 }}
          >
            All existing data in this table will be permanently deleted before
            the new data is inserted. This cannot be undone.
          </Typography>
        </Paper>

        {error && (
          <Alert severity="error" style={{ marginBottom: 12 }}>
            {error}
          </Alert>
        )}
        {loading && (
          <Box className={classes.progressWrap}>
            <LinearProgress />
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 8 }}
            >
              Importing data… this may take a minute for large files.
            </Typography>
          </Box>
        )}

        <div className={classes.stepActions}>
          <Button onClick={() => setActiveStep(3)} disabled={loading}>
            Back
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={loading}
            onClick={() => setConfirmOpen(true)}
          >
            Import Data
          </Button>
        </div>

        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Confirm Import</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to truncate{" "}
              <strong>{selectedPredictor?.label}</strong> and insert{" "}
              <strong>{rowCount}</strong> rows?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={handleExecute}
              color="secondary"
              variant="contained"
            >
              Yes, Import
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    );
  };

  const stepContent = [
    renderStep0,
    renderStep1,
    renderStep2,
    renderStep3,
    renderStep4,
  ];

  return (
    <div className={classes.root}>
      <Typography variant="h5" gutterBottom>
        Predictor Data Import
      </Typography>
      <Stepper
        activeStep={activeStep}
        className={classes.stepper}
        alternativeLabel
      >
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {stepContent[activeStep]?.()}
    </div>
  );
};

export default PredictorImport;
